

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);export const MESSAGE_ARTIFACT_TIMELINE_TYPE = 'message_artifact';

function resolveObject(value) {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
  ) ? value : null;
}

function resolveString(value) {
  return String(value ?? '').trim();
}

export function resolveArtifactDocumentFromCanvasPayload(canvasPayload) {
  const payload = resolveObject(canvasPayload);
  if (!payload) {
    return null;
  }
  const directArtifact = resolveObject(payload.artifact_document);
  if (directArtifact) {
    return directArtifact;
  }
  const canvasState = resolveObject(payload.canvas_state);
  return resolveObject(canvasState?.artifact_document);
}

function resolveArtifactTargetTab(canvasPayload) {
  const payload = resolveObject(canvasPayload);
  const uiSignal = resolveObject(payload?.ui_signal);
  return resolveString(uiSignal?.active_tab) || 'requirement';
}

function resolveArtifactContent(artifactDocument) {
  const latestRevision = resolveObject(artifactDocument?.latest_revision);
  const content = [
    latestRevision?.content,
    artifactDocument?.content,
    artifactDocument?.content_snapshot,
    artifactDocument?.summary,
  ].find((value) => String(value ?? '').trim());
  return String(content ?? '');
}

function resolvePackageCount(artifactDocument) {
  const packages = Array.isArray(artifactDocument?.procurement_packages)
    ? artifactDocument.procurement_packages
    : [];
  return packages.length;
}

function resolveAnalysisPayloadContent(analysisPayload) {
  const payload = resolveObject(analysisPayload);
  return String(payload?.markdown || payload?.content || payload?.text || '');
}

function resolveAnalysisPayloadDocument(analysisPayload) {
  const payload = resolveObject(analysisPayload);
  return resolveObject(payload?.document);
}

function resolveAnalysisTitle({ analysisPayload, content }) {
  const document = resolveAnalysisPayloadDocument(analysisPayload);
  const documentTitle = resolveString(document?.title);
  if (documentTitle) {
    return documentTitle;
  }
  const firstHeading = String(content || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line);
  return resolveString(firstHeading?.replace(/^#+\s*/, '')) || '分析报告';
}

function resolveArtifactAvailability({ artifactId, content }) {
  const canOpen = Boolean(resolveString(artifactId));
  const canDownload = Boolean(String(content ?? '').trim());
  const reasonCodes = [];
  if (!canOpen) {
    reasonCodes.push('artifact_id_missing');
  }
  if (!canDownload) {
    reasonCodes.push('download_content_missing');
  }
  if (reasonCodes.length === 0) {
    reasonCodes.push('passed');
  }
  return {
    can_open: canOpen,
    can_download: canDownload,
    reason_codes: reasonCodes,
  };
}

function resolveContentFramework(canvasPayload, artifactDocument) {
  const payload = resolveObject(canvasPayload);
  const canvasState = resolveObject(Object(payload).canvas_state);
  const requirementSummary = resolveObject(Object(payload).requirement_summary)
    || resolveObject(Object(canvasState).requirement_summary);
  return resolveObject(Object(payload).content_framework)
    || resolveObject(Object(canvasState).content_framework)
    || resolveObject(Object(requirementSummary).content_framework)
    || resolveObject(Object(artifactDocument).content_framework);
}

function isTemplateOnlyProjection(canvasPayload, artifactDocument) {
  const contentFramework = resolveContentFramework(canvasPayload, artifactDocument);
  const signals = resolveObject(contentFramework?.signals);
  return signals?.has_model_content === false;
}

function shouldRenderAnalysisArtifactCard({ analysisPayload, messageContent }) {
  const payload = resolveObject(analysisPayload);
  const visibility = resolveObject(payload?.timeline_visibility);
  if (visibility?.artifact_card === false) {
    return false;
  }
  return Boolean(resolveString(messageContent) || resolveAnalysisPayloadContent(payload));
}

function shouldRenderArtifactCard(canvasPayload, artifactDocument) {
  const payload = resolveObject(canvasPayload);
  const visibility = resolveObject(Object(payload).timeline_visibility);
  const visibilityReason = resolveString(Object(visibility).reason);
  if (everyTruthy(() => (Object(visibility).artifact_card === false), () => (visibilityReason !== 'canvas_direct_edit'))) {
    return false;
  }
  const latestRevision = resolveObject(Object(artifactDocument).latest_revision);
  const sourceKind = resolveString(Object(latestRevision).source_kind);
  const artifactStatus = resolveString(Object(artifactDocument).status);
  const artifactSource = resolveString(Object(artifactDocument).source);
  const revisionDiff = resolveString(Object(latestRevision).diff);
  const templateOnlyProjection = isTemplateOnlyProjection(payload, artifactDocument);
  if (everyTruthy(() => (artifactStatus === 'projected'), () => (templateOnlyProjection))) {
    return false;
  }
  if (everyTruthy(() => (firstTruthy(() => (artifactSource === 'canonical_scope'), () => (revisionDiff === 'canonical_scope'))), () => (templateOnlyProjection))) {
    return false;
  }
  if (revisionDiff === 'domain_template_projection') {
    return false;
  }
  if (everyTruthy(() => (sourceKind === 'canonical_scope'), () => (templateOnlyProjection))) {
    return false;
  }
  return true;
}

export function buildMessageAnalysisArtifactItem({
  analysisPayload,
  id,
  roundKey,
  source = '',
  messageContent = '',
}) {
  const payload = resolveObject(analysisPayload);
  if (firstTruthy(() => (!payload), () => (!shouldRenderAnalysisArtifactCard({ analysisPayload: payload, messageContent })))) {
    return null;
  }
  const content = resolveAnalysisPayloadContent(payload);
  if (!content.trim()) {
    return null;
  }
  const document = resolveAnalysisPayloadDocument(payload);
  const artifactId = firstTruthy(() => (resolveString(payload.artifact_id || Object(document).artifact_id)), () => (`analysis::${resolveString(id) || 'latest'}`));
  const title = resolveAnalysisTitle({ analysisPayload: payload, content });
  const availability = resolveArtifactAvailability({ artifactId, content });
  return {
    id: `message-analysis-artifact-${firstTruthy(() => (resolveString(id)), () => (artifactId))}`,
    type: MESSAGE_ARTIFACT_TIMELINE_TYPE,
    artifact_id: artifactId,
    artifact_type: 'analysis_report',
    kind: 'ArtifactDocument',
    title,
    content,
    content_format: 'markdown',
    source: firstTruthy(() => (resolveString(payload.source)), () => (resolveString(source))),
    status: firstTruthy(() => (resolveString(payload.status)), () => ('accepted')),
    template_key: resolveString(firstTruthy(() => (Object(document).template_id), () => (payload.template_id))),
    package_count: 0,
    target_tab: 'analysis',
    can_open: availability.can_open,
    can_download: availability.can_download,
    availability,
    roundKey: resolveString(roundKey),
    action: {
      type: 'open_artifact_document',
      artifact_id: artifactId,
      active_tab: 'analysis',
      can_open: availability.can_open,
    },
  };
}

export function buildMessageArtifactItem({
  canvasPayload,
  id,
  roundKey,
  source = '',
}) {
  const artifactDocument = resolveArtifactDocumentFromCanvasPayload(canvasPayload);
  const artifactId = resolveString(Object(artifactDocument).artifact_id);
  if (!artifactId) {
    return null;
  }
  if (!shouldRenderArtifactCard(canvasPayload, artifactDocument)) {
    return null;
  }
  const title = firstTruthy(() => (resolveString(Object(artifactDocument).title)), () => ('需求文档'));
  const targetTab = resolveArtifactTargetTab(canvasPayload);
  const content = resolveArtifactContent(artifactDocument);
  const availability = resolveArtifactAvailability({ artifactId, content });
  return {
    id: `message-artifact-${firstTruthy(() => (resolveString(id)), () => (artifactId))}`,
    type: MESSAGE_ARTIFACT_TIMELINE_TYPE,
    artifact_id: artifactId,
    artifact_type: resolveString(Object(artifactDocument).artifact_type),
    kind: firstTruthy(() => (resolveString(Object(artifactDocument).kind)), () => ('ArtifactDocument')),
    title,
    content,
    content_format: firstTruthy(() => (resolveString(Object(artifactDocument).content_format)), () => ('markdown')),
    source: firstTruthy(() => (resolveString(Object(artifactDocument).source)), () => (resolveString(source))),
    status: resolveString(Object(artifactDocument).status),
    template_key: resolveString(Object(artifactDocument).template_key),
    package_count: resolvePackageCount(artifactDocument),
    target_tab: targetTab,
    can_open: availability.can_open,
    can_download: availability.can_download,
    availability,
    roundKey: resolveString(roundKey),
    action: {
      type: 'open_artifact_document',
      artifact_id: artifactId,
      active_tab: targetTab,
      can_open: availability.can_open,
    },
  };
}

function resolvePostGenerationContent(postGenerationResult) {
  const payload = resolveObject(postGenerationResult);
  return String(payload?.artifact_markdown || payload?.content || '');
}

function resolvePostGenerationTitle({ descriptor, content, postGenerationResult }) {
  const title = resolveString(Object(descriptor).title);
  if (title) {
    return title;
  }
  const summary = resolveObject(Object(postGenerationResult).timeline_summary);
  const summaryText = resolveString(Object(summary).text);
  if (summaryText) {
    return summaryText;
  }
  const firstHeading = String(content || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line);
  return resolveString(firstHeading?.replace(/^#+\s*/, '')) || resolveString(Object(descriptor).artifact_type);
}

function resolvePostGenerationTargetTab(descriptor) {
  return resolveString(descriptor?.target_tab) || 'artifact';
}

export function buildMessagePostGenerationArtifactItem({
  postGenerationResult,
  id,
  roundKey,
  source = '',
}) {
  const payload = resolveObject(postGenerationResult);
  if (firstTruthy(() => (!payload), () => (resolveString(payload.status) !== 'completed'))) {
    return null;
  }
  const descriptor = resolveObject(payload.artifact_descriptor);
  const artifactId = resolveString(firstTruthy(() => (Object(descriptor).artifact_id), () => (payload.artifact_id)));
  const content = resolvePostGenerationContent(payload);
  if (firstTruthy(() => (!artifactId), () => (!content.trim()))) {
    return null;
  }
  const targetTab = resolvePostGenerationTargetTab(descriptor);
  const availability = resolveArtifactAvailability({ artifactId, content });
  return {
    id: `message-post-generation-artifact-${firstTruthy(() => (resolveString(id)), () => (artifactId))}`,
    type: MESSAGE_ARTIFACT_TIMELINE_TYPE,
    artifact_id: artifactId,
    artifact_type: resolveString(firstTruthy(() => (Object(descriptor).artifact_type), () => (payload.artifact_type))),
    kind: 'ArtifactDocument',
    title: resolvePostGenerationTitle({ descriptor, content, postGenerationResult: payload }),
    content,
    content_format: 'markdown',
    source: firstTruthy(() => (resolveString(source)), () => ('post_generation')),
    status: firstTruthy(() => (resolveString(Object(descriptor).status)), () => ('accepted')),
    template_key: resolveString(firstTruthy(() => (Object(descriptor).template_key), () => (payload.template_key))),
    package_count: 0,
    target_tab: targetTab,
    can_open: availability.can_open,
    can_download: availability.can_download,
    availability,
    roundKey: resolveString(roundKey),
    action: {
      type: 'open_artifact_document',
      artifact_id: artifactId,
      active_tab: targetTab,
      can_open: availability.can_open,
    },
  };
}
